import { createGlobalStyle } from "styled-components";

const GlobalStyles = createGlobalStyle`
  :root {
    /* ASKK Brand Colors - Inspired by product packaging */
    --primary-blue: #00BFFF;
    --secondary-blue: #1E90FF;
    --deep-blue: #0066CC;
    --accent-pink: #E91E63;
    --accent-yellow: #FFD700;
    --accent-orange: #FF8C00;
    --success-green: #4CAF50;
    --white: #FFFFFF;
    --light-gray: #F8F9FA;
    --medium-gray: #6C757D;
    --dark-gray: #343A40;
    --text-primary: #2C3E50;
    --text-secondary: #5A6C7D;
    
    /* Gradients */
    --gradient-primary: linear-gradient(135deg, var(--primary-blue), var(--secondary-blue));
    --gradient-accent: linear-gradient(135deg, var(--accent-pink), #F06292);
    --gradient-warm: linear-gradient(135deg, var(--accent-orange), var(--accent-yellow));
    --gradient-cool: linear-gradient(135deg, var(--primary-blue), var(--deep-blue));
    
    /* Shadows */
    --shadow-light: 0 2px 10px rgba(0, 191, 255, 0.1);
    --shadow-medium: 0 5px 20px rgba(0, 191, 255, 0.15);
    --shadow-heavy: 0 10px 30px rgba(0, 191, 255, 0.2);
    
    /* Responsive Grid System Variables */
    --container-max-width: 1200px;
    --container-padding-xl: 20px;
    --container-padding-lg: 18px;
    --container-padding-md: 16px;
    --container-padding-sm: 12px;
    
    /* Grid Gaps */
    --grid-gap-xl: 3rem;
    --grid-gap-lg: 2.5rem;
    --grid-gap-md: 2rem;
    --grid-gap-sm: 1.5rem;
    --grid-gap-xs: 1rem;
    
    /* Section Spacing */
    --section-padding-xl: 80px;
    --section-padding-lg: 70px;
    --section-padding-md: 60px;
    --section-padding-sm: 40px;
    
    /* Breakpoints */
    --breakpoint-xl: 1200px;
    --breakpoint-lg: 1024px;
    --breakpoint-md: 768px;
    --breakpoint-sm: 480px;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    line-height: 1.6;
    color: var(--text-primary);
    background-color: var(--white);
    font-weight: 400;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  html {
    scroll-behavior: smooth;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  button {
    border: none;
    cursor: pointer;
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  .container {
    max-width: var(--container-max-width);
    margin: 0 auto;
    padding: 0 var(--container-padding-xl);
    
    @media (max-width: 1024px) {
      padding: 0 var(--container-padding-lg);
    }
    
    @media (max-width: 768px) {
      padding: 0 var(--container-padding-md);
    }
    
    @media (max-width: 480px) {
      padding: 0 var(--container-padding-sm);
    }
  }

  .section-padding {
    padding: var(--section-padding-xl) 0;
    
    @media (max-width: 1024px) {
      padding: var(--section-padding-lg) 0;
    }
    
    @media (max-width: 768px) {
      padding: var(--section-padding-md) 0;
    }
    
    @media (max-width: 480px) {
      padding: var(--section-padding-sm) 0;
    }
  }

  .text-center {
    text-align: center;
  }

  .mb-4 {
    margin-bottom: 2rem;
  }

  .mb-8 {
    margin-bottom: 4rem;
  }

  /* Enhanced Grid System */
  .grid {
    display: grid;
    gap: var(--grid-gap-md);
  }
  
  .grid-gap-xl { gap: var(--grid-gap-xl); }
  .grid-gap-lg { gap: var(--grid-gap-lg); }
  .grid-gap-md { gap: var(--grid-gap-md); }
  .grid-gap-sm { gap: var(--grid-gap-sm); }
  .grid-gap-xs { gap: var(--grid-gap-xs); }

  .grid-2 {
    grid-template-columns: repeat(2, 1fr);
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--grid-gap-sm);
    }
  }

  .grid-3 {
    grid-template-columns: repeat(3, 1fr);
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(2, 1fr);
      gap: var(--grid-gap-sm);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--grid-gap-sm);
    }
  }

  .grid-4 {
    grid-template-columns: repeat(4, 1fr);
    
    @media (max-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
      gap: var(--grid-gap-sm);
    }
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
      gap: var(--grid-gap-sm);
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: var(--grid-gap-xs);
    }
  }
  
  /* Auto-fit grids for flexible layouts */
  .grid-auto-fit-250 {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    
    @media (max-width: 768px) {
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: var(--grid-gap-sm);
    }
    
    @media (max-width: 480px) {
      grid-template-columns: 1fr;
      gap: var(--grid-gap-xs);
    }
  }
  
  .grid-auto-fit-300 {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      gap: var(--grid-gap-sm);
    }
  }
  
  /* Additional responsive utilities */
  /* Typography and Visual Hierarchy */
  .text-responsive {
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
    
    @media (max-width: 480px) {
      font-size: 0.85rem;
    }
  }
  
  .heading-responsive {
    @media (max-width: 768px) {
      font-size: 1.8rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.5rem;
    }
  }
  
  .section-title {
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--text-primary);
    text-align: center;
    margin-bottom: var(--grid-gap-md);
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.75rem;
    }
  }
  
  .section-subtitle {
    font-size: 1.1rem;
    color: var(--text-secondary);
    text-align: center;
    max-width: 600px;
    margin: 0 auto var(--grid-gap-xl);
    line-height: 1.6;
  }
  
  /* Content Alignment and Spacing */
  .content-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .items-start { align-items: flex-start; }
  .items-center { align-items: center; }
  .items-end { align-items: flex-end; }
  
  .justify-start { justify-content: flex-start; }
  .justify-center { justify-content: center; }
  .justify-end { justify-content: flex-end; }
  .justify-between { justify-content: space-between; }
  
  /* Margin and Padding Utilities */
  .mt-xl { margin-top: var(--grid-gap-xl); }
  .mt-lg { margin-top: var(--grid-gap-lg); }
  .mt-md { margin-top: var(--grid-gap-md); }
  .mt-sm { margin-top: var(--grid-gap-sm); }
  
  .mb-xl { margin-bottom: var(--grid-gap-xl); }
  .mb-lg { margin-bottom: var(--grid-gap-lg); }
  .mb-md { margin-bottom: var(--grid-gap-md); }
  .mb-sm { margin-bottom: var(--grid-gap-sm); }
  
  /* Touch-friendly buttons */
  .btn-touch {
    min-height: 44px;
    min-width: 44px;
    
    @media (max-width: 768px) {
      padding: 0.75rem 1.5rem;
    }
  }
`;

export default GlobalStyles;
